import { useQueries } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { HttpStatusCode, PejHttpError } from '@common-lib';

import { useRoot } from '@frontend/utils';

import { makeShopQueryOptions } from './use-shop-query';

export interface ShopsStatusMapItem {
  shopId: string;
  enabled: boolean;
}

export const useShopsStatusMap = (
  items: ShopsStatusMapItem[],
): Map<string, boolean> => {
  const { removeLocalSession } = useRoot();

  const results = useQueries({
    queries: items.map((item) => ({
      ...makeShopQueryOptions(item.shopId),
      enabled: item.enabled,
    })),
  });

  const hasAuthError = results.some(
    (r) =>
      r.isError &&
      r.error instanceof PejHttpError &&
      (r.error.statusCode === HttpStatusCode.UNAUTHORIZED ||
        r.error.statusCode === HttpStatusCode.LOCKED),
  );

  useEffect(() => {
    if (hasAuthError) {
      removeLocalSession();
    }
  }, [hasAuthError, removeLocalSession]);

  return useMemo(() => {
    const map = new Map<string, boolean>();
    items.forEach((item, i) => {
      if (item.shopId) {
        const result = results[i];
        // Default to online (active) while pending, errored, or unknown so
        // ordering is never blocked spuriously.
        map.set(item.shopId, result?.data?.active ?? true);
      }
    });
    return map;
  }, [items, results]);
};
